import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from 'providers/AuthProvider';
import {
  getEmbedMechanism,
  isAuthNavigation,
  isEmbedHostLanding,
  wrapWithSso,
} from './amboss.config';
import { getAmbossDataGatePhase, subscribeToAmbossDataGate } from './ambossDataGate';
import {
  isAmbossSessionEstablished,
  markAmbossSessionEstablished,
  resetAmbossSessionState,
  subscribeToAmbossSessionState,
} from './ambossSessionState';

const LOAD_TIMEOUT_MS = 15000;
const MAX_SSO_HEAL_ATTEMPTS = 2;
const ERR_ABORTED = -3;
const AUTH_PARK_GRACE_MS = 3000;
const BLANK_PAGE_GRACE_MS = 3000;
const MAX_BLANK_PAGE_RECOVERIES = 2;
const BLANK_PAGE_PROBE = `(() => {
  const body = document.body;
  if (!body) return true;
  if (body.innerText.trim()) return false;
  return !body.querySelector('img,svg,canvas,video,iframe,embed,object,input,button,select,textarea');
})()`;

interface WebviewNavigationDetail {
  url?: string;
  isMainFrame?: boolean;
  isSameDocument?: boolean;
}

function extractCid(url: string): string | null {
  try {
    return new URL(url).searchParams.get('cid');
  } catch {
    return null;
  }
}

export type AmbossEmbedStatus = 'loading' | 'ready' | 'error';

export function useAmbossEmbed({
  url,
  reloadToken,
  onCid,
}: {
  url: string;
  reloadToken?: number;
  onCid?: (cid: string) => void;
}) {
  const { refreshEntraSession } = useAuth();
  const mechanism = getEmbedMechanism();
  const usesWebview = mechanism === 'webview';
  const dataGatePhase = useSyncExternalStore(subscribeToAmbossDataGate, getAmbossDataGatePhase);
  const [status, setStatus] = useState<AmbossEmbedStatus>('loading');
  const [attempt, setAttempt] = useState(0);
  const sessionReady = useSyncExternalStore(
    subscribeToAmbossSessionState,
    isAmbossSessionEstablished
  );
  const [ssoMode, setSsoMode] = useState<'pending' | 'sso' | 'plain'>('pending');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const webviewRef = useRef<HTMLElement | null>(null);
  const statusRef = useRef(status);
  statusRef.current = status;
  const ssoModeRef = useRef(ssoMode);
  ssoModeRef.current = ssoMode;
  const healAttemptsRef = useRef(0);
  const blankRecoveriesRef = useRef(0);
  const blankTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Applied on did-stop-loading: the src swap caused by marking the session
  // established must never abort an in-flight load.
  const pendingSessionReadyRef = useRef(false);
  const forceEntraRefreshRef = useRef(false);
  const authParkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const webviewSrc = sessionReady || ssoMode === 'plain' ? url : wrapWithSso(url);
  const needsEntraRefresh = usesWebview && !sessionReady;
  const canLoadWebview =
    !usesWebview || (dataGatePhase === 'ready' && (!needsEntraRefresh || ssoMode !== 'pending'));

  useEffect(() => {
    setStatus('loading');
    healAttemptsRef.current = 0;
    blankRecoveriesRef.current = 0;
    pendingSessionReadyRef.current = false;
  }, [url, reloadToken]);

  useEffect(() => {
    if (!needsEntraRefresh) return undefined;
    let cancelled = false;
    setSsoMode('pending');
    const force = forceEntraRefreshRef.current;
    forceEntraRefreshRef.current = false;
    void refreshEntraSession(force ? { force } : undefined).then((outcome) => {
      if (!cancelled) setSsoMode(outcome === 'success' ? 'sso' : 'plain');
    });
    return () => {
      cancelled = true;
    };
  }, [needsEntraRefresh, attempt, url, reloadToken, refreshEntraSession]);

  // The Entra refresh is unbounded (the user may be typing credentials), so
  // the timeout arms only once the webview may load.
  useEffect(() => {
    if (status !== 'loading' || !canLoadWebview) return undefined;
    const timeout = setTimeout(() => setStatus('error'), LOAD_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [status, url, reloadToken, attempt, canLoadWebview]);

  // React's synthetic onLoad/onError do not fire reliably on <iframe> (jsdom and
  // some browsers), so bind the load/error listeners to the node directly.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;
    const onLoad = () => setStatus('ready');
    const onError = () => setStatus('error');
    iframe.addEventListener('load', onLoad);
    iframe.addEventListener('error', onError);
    return () => {
      iframe.removeEventListener('load', onLoad);
      iframe.removeEventListener('error', onError);
    };
  }, [attempt, url, reloadToken]);

  useEffect(() => {
    const webview = webviewRef.current;
    if (!webview) return undefined;
    const view = webview as HTMLElement & {
      stop?: () => void;
      getURL?: () => string;
      executeJavaScript?: (code: string) => Promise<unknown>;
    };
    const clearAuthParkTimer = () => {
      if (authParkTimerRef.current === null) return;
      clearTimeout(authParkTimerRef.current);
      authParkTimerRef.current = null;
    };
    const clearBlankPageTimer = () => {
      if (blankTimerRef.current === null) return;
      clearTimeout(blankTimerRef.current);
      blankTimerRef.current = null;
    };
    const probeBlankPage = (): Promise<boolean> => {
      if (typeof view.executeJavaScript !== 'function') return Promise.resolve(false);
      return view
        .executeJavaScript(BLANK_PAGE_PROBE)
        .then((result) => result === true)
        .catch(() => false);
    };
    const armBlankPageWatchdog = () => {
      clearBlankPageTimer();
      const currentUrl = view.getURL?.();
      if (currentUrl && isAuthNavigation(currentUrl)) return;
      blankTimerRef.current = setTimeout(() => {
        blankTimerRef.current = null;
        void probeBlankPage().then((blank) => {
          if (!blank || statusRef.current === 'error') return;
          if (blankRecoveriesRef.current >= MAX_BLANK_PAGE_RECOVERIES) {
            setStatus('error');
            return;
          }
          blankRecoveriesRef.current += 1;
          setStatus('loading');
          setAttempt((a) => a + 1);
        });
      }, BLANK_PAGE_GRACE_MS);
    };
    const fallBackToPlain = () => {
      setSsoMode('plain');
      setStatus('loading');
    };
    const healSso = (): boolean => {
      if (healAttemptsRef.current >= MAX_SSO_HEAL_ATTEMPTS) return false;
      healAttemptsRef.current += 1;
      forceEntraRefreshRef.current = true;
      // Unmount ('pending') before the reset re-renders, so no magic-link
      // load starts with the known-stale cookies.
      setSsoMode('pending');
      resetAmbossSessionState();
      setAttempt((a) => a + 1);
      return true;
    };
    const onReady = () => {
      if (ssoModeRef.current === 'sso' && !isAmbossSessionEstablished()) return;
      setStatus('ready');
    };
    const onFail = (event: Event) => {
      if (statusRef.current !== 'loading') return;
      const detail = event as unknown as { isMainFrame?: boolean; errorCode?: number };
      if (detail.isMainFrame === false || detail.errorCode === ERR_ABORTED) return;
      setStatus('error');
    };
    const onStartNavigation = (event: Event) => {
      const detail = event as unknown as WebviewNavigationDetail;
      if (detail.isMainFrame === false || detail.isSameDocument === true || !detail.url) return;
      clearAuthParkTimer();
      clearBlankPageTimer();
      if (ssoModeRef.current === 'plain') return;
      if (!isAmbossSessionEstablished()) return;
      if (!isAuthNavigation(detail.url)) return;
      view.stop?.();
      setStatus('loading');
      if (!healSso()) fallBackToPlain();
    };
    const onNavigate = (event: Event) => {
      const navUrl = (event as unknown as { url?: string }).url;
      const cid = navUrl ? extractCid(navUrl) : null;
      if (cid) onCid?.(cid);
      if (!navUrl) return;
      if (ssoModeRef.current === 'plain') return;
      if (!isAmbossSessionEstablished() && isEmbedHostLanding(navUrl)) {
        pendingSessionReadyRef.current = true;
      }
    };
    const onStopLoading = () => {
      armBlankPageWatchdog();
      if (pendingSessionReadyRef.current) {
        pendingSessionReadyRef.current = false;
        markAmbossSessionEstablished();
        return;
      }
      if (ssoModeRef.current !== 'sso' || isAmbossSessionEstablished()) return;
      const currentUrl = view.getURL?.();
      if (!currentUrl || !isAuthNavigation(currentUrl)) return;
      clearAuthParkTimer();
      authParkTimerRef.current = setTimeout(() => {
        authParkTimerRef.current = null;
        if (statusRef.current !== 'loading') return;
        if (!healSso()) fallBackToPlain();
      }, AUTH_PARK_GRACE_MS);
    };
    webview.addEventListener('dom-ready', onReady);
    webview.addEventListener('did-fail-load', onFail);
    webview.addEventListener('did-start-navigation', onStartNavigation);
    webview.addEventListener('did-navigate', onNavigate);
    webview.addEventListener('did-navigate-in-page', onNavigate);
    webview.addEventListener('did-stop-loading', onStopLoading);
    return () => {
      clearAuthParkTimer();
      clearBlankPageTimer();
      webview.removeEventListener('dom-ready', onReady);
      webview.removeEventListener('did-fail-load', onFail);
      webview.removeEventListener('did-start-navigation', onStartNavigation);
      webview.removeEventListener('did-navigate', onNavigate);
      webview.removeEventListener('did-navigate-in-page', onNavigate);
      webview.removeEventListener('did-stop-loading', onStopLoading);
    };
  }, [attempt, url, reloadToken, onCid, canLoadWebview]);

  const retry = () => {
    setStatus('loading');
    setAttempt((a) => a + 1);
  };

  return {
    mechanism,
    status,
    retry,
    frameKey: `${attempt}-${reloadToken ?? 0}`,
    iframeRef,
    webviewRef,
    webviewSrc,
    canLoadWebview,
  };
}
